import { useState } from 'react'
import type { ApiKey } from '../types/api'
import { secretLabel, secretState, type KeyChain } from '../lib/keyChains'
import { lastUsedLabel } from '../lib/lastUsed'
import Timestamp from './Timestamp'

interface SecretListProps {
  chain: KeyChain
  usageTrackedSince: string | null
  now: Date
  onDeleteSecret?: (secret: ApiKey) => void
}

/** A rotated key's secrets, collapsed until opened. */
export default function SecretList({ chain, usageTrackedSince, now, onDeleteSecret }: SecretListProps) {
  const [open, setOpen] = useState(false)
  const listId = `secrets-${chain.id}`
  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        All secrets ({chain.secrets.length})
      </button>
      {open && (
        <ul id={listId} className="mt-2 space-y-2">
          {chain.secrets.map((secret) => {
            const used = lastUsedLabel(secret, usageTrackedSince, now)
            return (
              <li key={secret.id} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span>
                    <span className="font-mono text-gray-900">{secretLabel(secret)}</span>{' '}
                    <span className="text-gray-600">{secretState(secret, chain)}</span>
                  </span>
                  {onDeleteSecret && (
                    <button
                      type="button"
                      onClick={() => onDeleteSecret(secret)}
                      aria-label={`Delete secret ${secretLabel(secret)}`}
                      className="font-medium text-red-600 hover:text-red-500"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <p className="text-gray-500">
                  Made <Timestamp value={secret.created_at} /> · expires <Timestamp value={secret.expires_at} />
                  {used && (
                    <>
                      {' '}· last used: <span title={used.title}>{used.text}</span>
                    </>
                  )}
                </p>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
